import type { FailedCreator, JobResponse, StreamedMessage, VideoSelection } from "@unbox/api";
import { AlertTriangle, Check, InfoCircle } from "@untitledui/icons";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast as sonnerToast } from "sonner";
import { invalidateVideoQueries } from "@/api/assurance/actions";
import { invalidateBlocklist } from "@/api/blocklist";
import { fetchJob, useActiveJobs } from "@/api/jobs";
import { getScopedMerchantClient } from "@/api/scoped-merchant-client";
import { invalidateCampaignQueries } from "@/api/video-campaigns";
import { BulkActionResultsDialog } from "@/components/assurance/bulk-action-results-dialog";
import { JobResultsDialog } from "@/components/assurance/job-results-dialog";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { ProgressBarBase } from "@/components/base/progress-indicators/progress-indicators";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { Dialog } from "@/components/ui/dialog";
import type { ActionType } from "@/lib/assurance/action-types";
import { ACTION_LABELS } from "@/lib/assurance/bulk-action-errors";
import { isJobBackedAction, isTerminalJobStatus, jobToast } from "@/lib/jobs";
import { useMerchant, useWorkspaceId } from "@/providers/MerchantProvider";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { useSSE } from "@/providers/sse-provider";
import { pluralize } from "@/utils/text";

type BulkActionCompletion = {
  totalProcessed: number;
  totalFailed: number;
  failedCreators: FailedCreator[];
};

type JobMeta = {
  jobId: string;
  actionType: ActionType;
  selection: VideoSelection | null;
};

type CompletedJob = JobMeta & { completion: BulkActionCompletion };

type BulkActionContextValue = {
  /**
   * Shows the toast for work a request just started. A jobs-table action passes the row the response
   * carried, or is looked up by id; anything else is tracked by the events it will publish.
   */
  startTracking: (jobId: string, actionType: ActionType, selection: VideoSelection | null, job?: JobResponse) => void;
  getCompletedJob: (jobId: string) => CompletedJob | undefined;
};

const BulkActionContext = createContext<BulkActionContextValue | undefined>(undefined);

const getLabels = (actionType: string) =>
  ACTION_LABELS[actionType] ?? {
    inProgress: "Processing...",
    success: "All items processed successfully",
    partialFailure: "Some items couldn't be processed",
  };

const isVideoBased = (actionType: string): boolean =>
  actionType === "demonetization_ticket" ||
  actionType === "takedown_request" ||
  actionType === "remove_from_ad_campaign";

const getUnitLabel = (actionType: string, count: number): string =>
  isVideoBased(actionType) ? pluralize(count, "video", "videos") : pluralize(count, "creator", "creators");

const ProgressToast = ({
  toastId,
  actionType,
  completed,
  total,
  failures,
  onDismiss,
}: {
  toastId: string | number;
  actionType: string;
  completed: number;
  total: number;
  failures: number;
  onDismiss?: () => void;
}) => {
  const labels = getLabels(actionType);
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="relative z-[var(--z-index)] flex max-w-full flex-col gap-4 rounded-xl bg-primary_alt p-4 shadow-none ring ring-secondary_alt xs:w-[var(--width)]">
      <div className="flex items-start gap-3 pr-8">
        <FeaturedIcon icon={InfoCircle} color="brand" theme="outline" size="md" />
        <div className="flex flex-1 flex-col gap-1 min-w-0">
          <p className="text-sm font-semibold text-fg-primary">{labels.inProgress}</p>
          <p className="text-sm text-fg-secondary">
            {completed} of {getUnitLabel(actionType, total)}
            {failures > 0 && <span className="text-fg-error-primary"> · {failures} failed</span>}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <ProgressBarBase value={completed} max={total || 1} />
        <span className="text-sm font-medium text-fg-secondary tabular-nums shrink-0">{percentage}%</span>
      </div>
      <div className="absolute top-2 right-2">
        <CloseButton
          onClick={() => {
            onDismiss?.();
            sonnerToast.dismiss(toastId);
          }}
          size="sm"
          label="Dismiss"
        />
      </div>
    </div>
  );
};

const CompletionToast = ({
  toastId,
  actionType,
  completion,
  onViewDetails,
}: {
  toastId: string | number;
  actionType: string;
  completion: BulkActionCompletion;
  onViewDetails: () => void;
}) => {
  const labels = getLabels(actionType);
  const hasFailures = completion.totalFailed > 0;
  const succeeded = completion.totalProcessed - completion.totalFailed;

  return (
    <div className="relative z-[var(--z-index)] flex max-w-full flex-col gap-3 rounded-xl bg-primary_alt p-4 shadow-none ring ring-secondary_alt xs:w-[var(--width)]">
      <div className="flex items-start gap-3 pr-8">
        <FeaturedIcon
          icon={hasFailures ? AlertTriangle : Check}
          color={hasFailures ? "warning" : "success"}
          theme="outline"
          size="md"
        />
        <div className="flex flex-1 flex-col gap-1 min-w-0">
          <p className="text-sm font-semibold text-fg-primary">
            {hasFailures ? labels.partialFailure : labels.success}
          </p>
          <p className="text-sm text-fg-secondary">
            {hasFailures
              ? `${succeeded} succeeded, ${pluralize(completion.totalFailed, "issue", "issues")}`
              : `${getUnitLabel(actionType, completion.totalProcessed)} processed`}
          </p>
        </div>
      </div>
      {hasFailures && (
        <div className="flex gap-3 pl-13">
          <Button onClick={onViewDetails} size="sm" color="link-color">
            View details
          </Button>
        </div>
      )}
      <div className="absolute top-2 right-2">
        <CloseButton onClick={() => sonnerToast.dismiss(toastId)} size="sm" label="Dismiss" />
      </div>
    </div>
  );
};

export const BulkActionProgressProvider = ({ children }: { children: React.ReactNode }) => {
  const activeJobsRef = useRef<Map<string, JobMeta>>(new Map());
  const completedJobsRef = useRef<Map<string, CompletedJob>>(new Map());
  const merchantId = useMerchant();
  const workspaceId = useWorkspaceId();
  const [dialogJob, setDialogJob] = useState<CompletedJob | null>(null);

  // Jobs-table jobs the page has decided to show. Fed by the active list, by the response that
  // started one, and by `job_updated`; the toasts render from this map alone, so it does not matter
  // which source a row arrived from or in what order.
  const [trackedJobs, setTrackedJobs] = useState<Map<string, JobResponse>>(new Map());
  const trackedJobsRef = useRef(trackedJobs);
  trackedJobsRef.current = trackedJobs;
  const dismissedJobsRef = useRef<Set<string>>(new Set());
  const [dialogJobRun, setDialogJobRun] = useState<JobResponse | null>(null);
  const { data: activeJobRuns } = useActiveJobs();

  const mergeJob = useCallback(
    (job: JobResponse) => {
      const toast = jobToast(job.kind);
      if (!toast || dismissedJobsRef.current.has(job.id)) return;
      const previous = trackedJobsRef.current.get(job.id);
      if (merchantId && isTerminalJobStatus(job.status) && (!previous || !isTerminalJobStatus(previous.status))) {
        toast.invalidate?.(merchantId, workspaceId);
      }
      setTrackedJobs((current) => new Map(current).set(job.id, { ...previous, ...job }));
    },
    [merchantId, workspaceId],
  );

  const dismissJob = useCallback((jobId: string) => {
    dismissedJobsRef.current.add(jobId);
    setTrackedJobs((current) => {
      const next = new Map(current);
      next.delete(jobId);
      return next;
    });
  }, []);

  useEffect(() => {
    for (const job of activeJobRuns ?? []) mergeJob(job);
  }, [activeJobRuns, mergeJob]);

  useEffect(() => {
    const handler = (e: Event) => {
      const jobId = (e as CustomEvent<{ jobId: string }>).detail.jobId;
      const job = completedJobsRef.current.get(jobId);
      if (job) setDialogJob(job);
      const run = trackedJobsRef.current.get(jobId);
      if (run) setDialogJobRun(run);
    };
    window.addEventListener("bulk-action-view-details", handler);
    return () => window.removeEventListener("bulk-action-view-details", handler);
  }, []);

  const openResultsDialog = useCallback((jobId: string) => {
    window.dispatchEvent(new CustomEvent("bulk-action-view-details", { detail: { jobId } }));
  }, []);

  useEffect(() => {
    for (const job of trackedJobs.values()) {
      const toast = jobToast(job.kind);
      if (!toast) continue;
      const { actionType } = toast;
      const toastId = `job-${job.id}`;

      if (isTerminalJobStatus(job.status)) {
        sonnerToast.custom(
          (id) => (
            <CompletionToast
              toastId={id}
              actionType={actionType}
              completion={{ totalProcessed: job.succeeded + job.failed, totalFailed: job.failed, failedCreators: [] }}
              onViewDetails={() => openResultsDialog(job.id)}
            />
          ),
          {
            id: toastId,
            duration: job.failed > 0 ? Number.POSITIVE_INFINITY : 8000,
            className: "pointer-events-auto",
            onDismiss: () => dismissJob(job.id),
            onAutoClose: () => dismissJob(job.id),
          },
        );
        continue;
      }

      sonnerToast.custom(
        (id) => (
          <ProgressToast
            toastId={id}
            actionType={actionType}
            completed={job.succeeded + job.failed}
            total={job.total ?? 0}
            failures={job.failed}
            onDismiss={() => dismissJob(job.id)}
          />
        ),
        { id: toastId, duration: Number.POSITIVE_INFINITY, className: "pointer-events-auto" },
      );
    }
  }, [trackedJobs, openResultsDialog, dismissJob]);

  const handleSSE = useCallback(
    (message: StreamedMessage) => {
      if (message.event === "job_updated") {
        if (!merchantId || dismissedJobsRef.current.has(message.jobId)) return;

        const known = trackedJobsRef.current.get(message.jobId);
        if (known) {
          mergeJob({
            ...known,
            status: message.status,
            total: message.total,
            succeeded: message.succeeded,
            failed: message.failed,
          });
          return;
        }

        // Started elsewhere, another tab or before this page loaded. The row has what the event lacks.
        fetchJob(getScopedMerchantClient(merchantId, workspaceId), message.jobId)
          .then(mergeJob)
          .catch(() => {});
        return;
      }

      if (message.event === "bulk_action_progress") {
        const meta = activeJobsRef.current.get(message.jobId);
        if (!meta) return;

        const toastId = `bulk-action-${message.jobId}`;
        sonnerToast.custom(
          (id) => (
            <ProgressToast
              toastId={id}
              actionType={meta.actionType}
              completed={message.completed}
              total={message.total}
              failures={message.failures}
            />
          ),
          { id: toastId, duration: Number.POSITIVE_INFINITY, className: "pointer-events-auto" },
        );
      }

      if (message.event === "bulk_action_complete") {
        const meta = activeJobsRef.current.get(message.jobId);
        if (!meta) return;

        const completion: BulkActionCompletion = {
          totalProcessed: message.totalProcessed,
          totalFailed: message.totalFailed,
          failedCreators: message.failedCreators,
        };

        completedJobsRef.current.set(message.jobId, { ...meta, completion });
        activeJobsRef.current.delete(message.jobId);

        const toastId = `bulk-action-${message.jobId}`;
        const completionDuration = completion.totalFailed > 0 ? Number.POSITIVE_INFINITY : 8000;

        sonnerToast.custom(
          (id) => (
            <CompletionToast
              toastId={id}
              actionType={meta.actionType}
              completion={completion}
              onViewDetails={() => openResultsDialog(message.jobId)}
            />
          ),
          { id: toastId, duration: completionDuration, className: "pointer-events-auto" },
        );

        if (merchantId) {
          if (meta.selection) {
            invalidateVideoQueries(merchantId, workspaceId, meta.selection);
          }
          if (meta.actionType === "message_creator" || meta.actionType === "invite_to_collaboration") {
            queryClient.invalidateQueries({ queryKey: ["creators_with_activities", merchantId] });
            queryClient.invalidateQueries({ queryKey: ["creator", merchantId] });
            queryClient.invalidateQueries({ queryKey: ["collaborations", merchantId] });
          }
          if (meta.actionType === "import_blocklist") {
            invalidateBlocklist(queryClient);
          }

          if (meta.actionType === "remove_from_ad_campaign") {
            // Refresh the "In ads campaign" pill, drawer campaigns and action history once the bulk
            // removal lands, so cross-page selections update without a manual reload (UNB-2024).
            invalidateCampaignQueries(merchantId, workspaceId);
          }
        }
      }
    },
    [merchantId, workspaceId, openResultsDialog, mergeJob],
  );

  useSSE(handleSSE);

  const startTracking = useCallback(
    (jobId: string, actionType: ActionType, selection: VideoSelection | null, job?: JobResponse) => {
      if (job) {
        mergeJob(job);
        return;
      }
      if (isJobBackedAction(actionType)) {
        if (merchantId) {
          fetchJob(getScopedMerchantClient(merchantId, workspaceId), jobId)
            .then(mergeJob)
            .catch(() => {});
        }
        return;
      }

      const meta: JobMeta = { jobId, actionType, selection };
      activeJobsRef.current.set(jobId, meta);

      const toastId = `bulk-action-${jobId}`;
      sonnerToast.custom(
        (id) => <ProgressToast toastId={id} actionType={actionType} completed={0} total={0} failures={0} />,
        { id: toastId, duration: Number.POSITIVE_INFINITY, className: "pointer-events-auto" },
      );
    },
    [merchantId, workspaceId, mergeJob],
  );

  const getCompletedJob = useCallback((jobId: string) => completedJobsRef.current.get(jobId), []);

  const value = useMemo(() => ({ startTracking, getCompletedJob }), [startTracking, getCompletedJob]);

  return (
    <BulkActionContext.Provider value={value}>
      {children}
      <Dialog open={!!dialogJob} onOpenChange={(open) => !open && setDialogJob(null)}>
        {dialogJob && (
          <BulkActionResultsDialog
            completion={dialogJob.completion}
            actionType={dialogJob.actionType}
            onClose={() => setDialogJob(null)}
          />
        )}
      </Dialog>
      <Dialog open={!!dialogJobRun} onOpenChange={(open) => !open && setDialogJobRun(null)}>
        {dialogJobRun && <JobResultsDialog job={dialogJobRun} onClose={() => setDialogJobRun(null)} />}
      </Dialog>
    </BulkActionContext.Provider>
  );
};

export const useBulkActionContext = () => {
  const context = useContext(BulkActionContext);
  if (!context) {
    throw new Error("useBulkActionContext must be used within BulkActionProgressProvider");
  }
  return context;
};
